import { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardActions, Box, Typography } from '@mui/material';
import { ProgressButton, Breadcrumbs, ECSServiceForm } from '@localstack/ui';
import { CreateECSServiceParams, UpdateECSServiceParams, ECSTaskDefinition } from '@localstack/types';
import { useRoutes, useAwsEffect, useAwsGetter, useAwsBatchIterator } from '@localstack/services';

import { DEFAULT_ECS_ROUTES } from '@localstack/constants';

import { ECSProps } from './types';

export const ECSClusterServiceUpsert = ({
  Layout,
  clientOverrides,
  routes = DEFAULT_ECS_ROUTES,
}: ECSProps): ReactElement => {
  const { goto } = useRoutes();

  const { clusterName, arn } = useParams<'clusterName' | 'arn'>();

  const { describeTaskDefinition } = useAwsEffect('ECS', ['describeTaskDefinition'], { clientOverrides });

  const { data: definitionArns } = useAwsGetter('ECS', 'listTaskDefinitions', [], { clientOverrides });

  const definitions = useAwsBatchIterator(
    definitionArns?.taskDefinitionArns,
    async (taskDefinition) => (await describeTaskDefinition({ taskDefinition })).taskDefinition as ECSTaskDefinition,
  );

  const {
    createService,
    updateService,
    isLoading: isServiceMutating,
  } = useAwsEffect('ECS', ['createService', 'updateService'], {
    revalidate: ['listServices', 'describeServices', 'listTasks'],
    clientOverrides,
  });

  const { data: services } = useAwsGetter(
    'ECS',
    'describeServices',
    [{ cluster: clusterName as string, services: [arn as string] }],
    { clientOverrides },
  );

  const service = services?.services?.[0];

  const handleCreate = async (data: CreateECSServiceParams) => {
    if (await createService(data)) {
      goto(routes.RESOURCES_ECS_CLUSTER_SERVICES, { clusterName });
    }
  };

  const handleUpdate = async (data: UpdateECSServiceParams) => {
    if (await updateService(data)) {
      goto(routes.RESOURCES_ECS_CLUSTER_SERVICE, { clusterName, arn });
    }
  };

  return (
    <Layout
      documentTitle="Service Details"
      title={
        <Box>
          <Typography variant="h4">Service Details</Typography>
          <Breadcrumbs
            mappings={[
              ['ECS', () => goto(routes.RESOURCES_ECS)],
              ['Clusters', () => goto(routes.RESOURCES_ECS_CLUSTERS)],
              [clusterName, () => goto(routes.RESOURCES_ECS_CLUSTER, { clusterName })],
              [arn, () => goto(routes.RESOURCES_ECS_CLUSTER_SERVICE, { clusterName, arn })],
              [arn ? 'Update' : 'Create Service', null],
            ]}
          />
        </Box>
      }
      actions={
        <ProgressButton type="submit" form="update" variant="outlined" color="primary" loading={isServiceMutating}>
          Submit
        </ProgressButton>
      }
    >
      <Card>
        <CardContent>
          <ECSServiceForm
            cluster={clusterName as string}
            service={service}
            definitions={definitions}
            loading={isServiceMutating}
            formId="update"
            clientOverrides={clientOverrides}
            onCreate={handleCreate}
            onUpdate={handleUpdate}
          />
        </CardContent>
        <CardActions>
          <ProgressButton type="submit" form="update" variant="contained" color="primary" loading={isServiceMutating}>
            Submit
          </ProgressButton>
        </CardActions>
      </Card>
    </Layout>
  );
};
